// src/components/seating-chart/user/useChartViewport.js  (NEW FILE)
//
// Same fit/zoom/pan behaviour as AdminChartEventSeatMap, extracted into a hook and
// extended with touch (one-finger pan, two-finger pinch) because buyers are mostly
// on phones. You can later point the admin map at this hook too.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const PAN_OVERSCROLL = 400;

const SEAT_RADIUS = 14; // keep equal to the value in ChartEventSeatPicker / AdminChartEventSeatMap
const SEAT_DIAMETER = SEAT_RADIUS * 2;

// on-screen seat diameter in px
const INITIAL_SEAT_PX = 36; // a small map never opens with seats bigger than this
const MIN_SEAT_PX = 4;     // how far a small map can zoom out
const MAX_SEAT_PX = 200;

const computeViewAtScale = (bounds, size, scale) => ({
  scale,
  x: size.width / 2 - (bounds.x + bounds.width / 2) * scale,
  y: size.height / 2 - (bounds.y + bounds.height / 2) * scale,
});

const computeScaleLimits = (fitScale) => {
  const initial = Math.min(fitScale, INITIAL_SEAT_PX / SEAT_DIAMETER);
  const min = Math.min(initial, MIN_SEAT_PX / SEAT_DIAMETER);
  const max = Math.max(MAX_SEAT_PX / SEAT_DIAMETER, initial * 1.5);
  return { initial, min, max };
};

const computeFit = (bounds, size, padding = 40) => {
  const scale = Math.min(
    (size.width - padding) / Math.max(bounds.width, 1),
    (size.height - padding) / Math.max(bounds.height, 1),
  );
  return {
    scale,
    x: size.width / 2 - (bounds.x + bounds.width / 2) * scale,
    y: size.height / 2 - (bounds.y + bounds.height / 2) * scale,
  };
};

const clampAxis = (pos, boundsMin, boundsSize, viewportSize, scale) => {
  const contentSize = boundsSize * scale;
  const halfRange = Math.abs(viewportSize - contentSize) / 2 + PAN_OVERSCROLL;
  const centered = viewportSize / 2 - (boundsMin + boundsSize / 2) * scale;
  return Math.min(centered + halfRange, Math.max(centered - halfRange, pos));
};

export const getWorldBounds = (chart) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const consider = (x, y, w = 0, h = 0) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  };
  (chart.seats ?? []).forEach((s) =>
    consider(s.x - SEAT_RADIUS, s.y - SEAT_RADIUS, SEAT_DIAMETER, SEAT_DIAMETER),
  );
  (chart.sections ?? []).forEach((s) => consider(s.x, s.y, s.width, s.height));
  (chart.canvasElements ?? []).forEach((e) => consider(e.x, e.y, e.width ?? 0, e.height ?? 0));
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: chart.canvasWidth, height: chart.canvasHeight };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export default function useChartViewport(chart) {
  const stageRef = useRef(null);
  const [container, setContainer] = useState(null); // use as <Box ref={setContainer}>
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [hasFit, setHasFit] = useState(false);
  // const fitScale = useRef(1);
  const limits = useRef({ min: 0.1, initial: 1, max: 4 });
  const viewRef = useRef(view);
  viewRef.current = view;

  const bounds = useMemo(() => (chart ? getWorldBounds(chart) : null), [chart]);

  useEffect(() => {
    if (!container) return undefined;
    const update = () => setSize({ width: container.clientWidth, height: container.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [container]);

  useEffect(() => {
    if (!bounds || !size.width || !size.height) return;
    const next = computeScaleLimits(computeFit(bounds, size).scale);
    limits.current = next;
    setView(computeViewAtScale(bounds, size, next.initial));
    setHasFit(true);
  }, [bounds, size]);

  const clamp = useCallback(
    (x, y, scale) => ({
      x: clampAxis(x, bounds.x, bounds.width, size.width, scale),
      y: clampAxis(y, bounds.y, bounds.height, size.height, scale),
    }),
    [bounds, size],
  );

  const zoomAt = useCallback(
    (pointer, nextScale) => {
      if (!bounds) return;
      const { x, y, scale } = viewRef.current;
      const { min, max } = limits.current;
      const scaleClamped = Math.min(max, Math.max(min, nextScale));

      if (scaleClamped <= min) {
        setView(computeViewAtScale(bounds, size, min)); // at the floor: recenter
        return;
      }
      const worldPt = { x: (pointer.x - x) / scale, y: (pointer.y - y) / scale };
      const raw = { x: pointer.x - worldPt.x * scaleClamped, y: pointer.y - worldPt.y * scaleClamped };
      setView({ scale: scaleClamped, ...clamp(raw.x, raw.y, scaleClamped) });
    },
    [bounds, size, clamp],
  );

  // ---- mouse wheel ------------------------------------------------------------
  const onWheel = useCallback(
    (e) => {
      e.evt.preventDefault();
      const pointer = stageRef.current.getPointerPosition();
      const dir = e.evt.deltaY > 0 ? -1 : 1;
      zoomAt(pointer, viewRef.current.scale * (1 + dir * 0.1));
    },
    [zoomAt],
  );

  // ---- mouse drag on empty canvas ---------------------------------------------
  const [isPanning, setIsPanning] = useState(false);
  const last = useRef({ x: 0, y: 0 });
  const dragged = useRef(false); // lets seats ignore the click that ends a pan

  const onMouseDown = useCallback((e) => {
    if (e.evt.button !== 0 || e.target !== e.target.getStage()) return;
    setIsPanning(true);
    dragged.current = false;
    last.current = { x: e.evt.clientX, y: e.evt.clientY };
  }, []);

  useEffect(() => {
    if (!isPanning) return undefined;
    const move = (e) => {
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      last.current = { x: e.clientX, y: e.clientY };
      dragged.current = true;
      setView((p) => ({ ...p, ...clamp(p.x + dx, p.y + dy, p.scale) }));
    };
    const up = () => setIsPanning(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isPanning, clamp]);

  // ---- touch: 1 finger pans, 2 fingers pinch-zoom ------------------------------
  const touch = useRef({ dist: 0, center: null, lastPan: null });

  const onTouchStart = useCallback((e) => {
    const t = e.evt.touches;
    dragged.current = false;
    if (t.length === 1) touch.current.lastPan = { x: t[0].clientX, y: t[0].clientY };
    if (t.length === 2) touch.current.lastPan = null;
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      const t = e.evt.touches;
      if (t.length === 2) {
        e.evt.preventDefault();
        const box = stageRef.current.container().getBoundingClientRect();
        const p1 = { x: t[0].clientX - box.left, y: t[0].clientY - box.top };
        const p2 = { x: t[1].clientX - box.left, y: t[1].clientY - box.top };
        const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        if (touch.current.dist) {
          dragged.current = true;
          zoomAt(center, viewRef.current.scale * (dist / touch.current.dist));
        }
        touch.current.dist = dist;
        touch.current.center = center;
      } else if (t.length === 1 && touch.current.lastPan) {
        const dx = t[0].clientX - touch.current.lastPan.x;
        const dy = t[0].clientY - touch.current.lastPan.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) dragged.current = true;
        touch.current.lastPan = { x: t[0].clientX, y: t[0].clientY };
        setView((p) => ({ ...p, ...clamp(p.x + dx, p.y + dy, p.scale) }));
      }
    },
    [zoomAt, clamp],
  );

  const onTouchEnd = useCallback(() => {
    touch.current = { dist: 0, center: null, lastPan: null };
  }, []);

  const zoomBy = useCallback(
    (factor) => zoomAt({ x: size.width / 2, y: size.height / 2 }, viewRef.current.scale * factor),
    [zoomAt, size],
  );
  const reset = useCallback(
    () => bounds && setView(computeViewAtScale(bounds, size, limits.current.initial)),
    [bounds, size],
  );

  return {
    stageRef,
    setContainer,
    size,
    view,
    hasFit,
    dragged,
    zoomBy,
    reset,
    stageProps: {
      width: size.width,
      height: size.height,
      x: view.x,
      y: view.y,
      scaleX: view.scale,
      scaleY: view.scale,
      onWheel,
      onMouseDown,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
}